import { useState } from 'react'
import { IconExternalLink } from '@tabler/icons-react'
import { ProductSummary } from 'shared-types'
import { Button } from '~/components/Button'
import { Link } from '~/components/Link'
import { ProductImage } from '~/components/ProductImage'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import { getImageSrc } from '~/helpers'
import { useFormatPrice } from '~/hooks'

export interface ProductSelectionCardProps {
  product: ProductSummary
  isSelected?: boolean
  onSelect?: () => Promise<boolean>
}

export const ProductSelectionCard = ({
  product,
  isSelected = false,
  onSelect,
}: ProductSelectionCardProps) => {
  const [loading, setLoading] = useState(false)
  const { formatPrice } = useFormatPrice()
  const { title, sku, thumbnail, finalPrice, url } = product

  const handleSelect = async () => {
    if (!onSelect || loading) {
      return
    }

    setLoading(true)
    await onSelect()
    setLoading(false)
  }

  return (
    <div className='flex items-start gap-4'>
      <Link
        href={url}
        target='_blank'
        className='block relative w-16 h-16 min-w-max aspect-square rounded-md bg-background-thumb'>
        <ProductImage
          src={getImageSrc(thumbnail?.src, '64')}
          addSrcSet={false}
          alt={title}
          fill
          style={{ objectFit: 'contain' }}
          sizes='64px'
        />
      </Link>
      <div className='flex-1 text-grey-900'>
        <div className='flex flex-col gap-2'>
          <Link
            href={url}
            target='_blank'
            className='relative block w-fit max-w-full pr-8'>
            <Typography
              tag={TypographyTag.p}
              variant={TypographyVariant.BodyRegularBold}>
              {title}
            </Typography>
            <span className='absolute block -top-0.5 right-0'>
              <IconExternalLink />
            </span>
          </Link>
          <Typography
            variant={TypographyVariant.BodyRegular}
            tag={TypographyTag.p}
            className='text-greyscale-600'>
            <span className='whitespace-nowrap font-bold'>CAT.NO: {sku}</span>
          </Typography>
          <Typography
            variant={TypographyVariant.BodyRegular}
            tag={TypographyTag.p}
            className='text-greyscale-600'>
            <span className='whitespace-nowrap font-bold'>
              Unit price: {formatPrice(finalPrice)}
            </span>
          </Typography>
        </div>
      </div>
      <div className='min-w-20'>
        {!isSelected && (
          <Button status='secondary' loading={loading} onClick={handleSelect}>
            Select
          </Button>
        )}
      </div>
    </div>
  )
}
